import type { Translate } from 'next-translate';
import React, {
	type FC,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Container } from '@/components/elements/Container';
import { MyImage } from '@/components/elements/MyImage';
import { useIsMobile } from '@/hooks';
import { getUrlParams } from '@/helpers';
import { LandingTool, type LandingTools } from '@/data/aiToolLandings';
import bgMobile from '@/public/images/landing/backgrounds/bg-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg.webp';
import { Listing } from './Listing';
import { ToolsTabs } from './ToolsTabs';
import styles from './ToolsExplorer.module.scss';

interface ToolsExplorerProps {
	data: LandingTools['hero'];
	t: Translate;
}

export const ToolsExplorer: FC<ToolsExplorerProps> = ({ data, t }) => {
	const isMobile = useIsMobile();
	const toolsContainerRef = useRef<HTMLDivElement>(null);

	const [activeTabId, setActiveTabId] = useState<string>(data.tabs[0].id);

	useEffect(() => {
		const { category } = getUrlParams();
		if (category && category !== 'search') {
			setActiveTabId(category);
		}
	}, []);

	const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
	const [isTabsSticky, setTabsSticky] = useState(false);
	const [activeFeature, setActiveFeature] = useState<LandingTool | null>(null);
	const [activePositioningId, setActivePositioningId] = useState<string | null>(
		null,
	);
	const [pinnedFeatureId, setPinnedFeatureId] = useState<string | null>(null);

	const currentTabId = hoveredTabId ?? activeTabId;

	// FEATURES: active feature to the start of array (only when pinned from search)
	const preparedFeaturesData = useMemo(() => {
		const listingValues = Object.values(data.toolsListing);

		const filteredTools = (
			currentTabId === 'all'
				? listingValues
				: listingValues.filter(item => item.tab === currentTabId)
		).flatMap(item => item.tools);

		const sorted = [...filteredTools].sort((a, b) =>
			a.title.localeCompare(b.title),
		);

		if (!pinnedFeatureId) return sorted;

		const activeItem = sorted.find(tool => tool.id === pinnedFeatureId);
		const otherItems = sorted.filter(tool => tool.id !== pinnedFeatureId);

		return activeItem ? [activeItem, ...otherItems] : sorted;
	}, [currentTabId, pinnedFeatureId, data.toolsListing]);

	// POSITIONING: active positioning to the start of array
	const preparedPositioningData = useMemo(() => {
		const sourceList = activeFeature
			? activeFeature.list
			: preparedFeaturesData.flatMap(l => l.list);

		if (!activePositioningId) return sourceList;

		const activeItem = sourceList.find(i => i.id === activePositioningId);
		const otherItems = sourceList.filter(i => i.id !== activePositioningId);

		return activeItem ? [activeItem, ...otherItems] : sourceList;
	}, [preparedFeaturesData, activeFeature, activePositioningId]);

	const scrollIntoView = useCallback(() => {
		const toolsContainerEl = toolsContainerRef.current;
		if (isMobile || !toolsContainerEl || !isTabsSticky) return;
		window?.scrollTo({ top: toolsContainerEl.offsetTop, behavior: 'smooth' });
	}, [isMobile, isTabsSticky]);

	return (
		<section className={styles['tools']}>
			<MyImage
				imageProps={{
					fill: true,
					src: isMobile ? bgMobile : bg,
					alt: '',
					title: '',
					'aria-hidden': 'true',
				}}
				settings={{
					classes: {
						picture: `${styles['tools__background']}`,
						image: styles['tools__background-img'],
					},
				}}
			/>

			<h1 className={styles['tools__title']}>{t(data.title)}</h1>

			<ToolsTabs
				data={data}
				activeTabId={activeTabId}
				setActiveTabId={setActiveTabId}
				isTabsSticky={isTabsSticky}
				setTabsSticky={setTabsSticky}
				hoveredTabId={hoveredTabId}
				setHoveredTabId={setHoveredTabId}
				activePositioningId={activePositioningId}
				setActivePositioningId={setActivePositioningId}
				setActiveFeature={setActiveFeature}
				setPinnedFeatureId={setPinnedFeatureId}
				scrollToolsIntoViewIfNeeded={scrollIntoView}
				toolsContainerRef={toolsContainerRef}
				t={t}
			/>

			<Container className={styles.tools__container} ref={toolsContainerRef}>
				<Listing
					isTabsSticky={isTabsSticky}
					activePositioningId={activePositioningId}
					activeFeature={activeFeature}
					setActiveFeature={setActiveFeature}
					setPinnedFeatureId={setPinnedFeatureId}
					featuresData={preparedFeaturesData}
					positioningData={preparedPositioningData}
					activeTabId={activeTabId}
					hoveredTabId={hoveredTabId}
					scrollToolsIntoViewIfNeeded={scrollIntoView}
				/>
			</Container>
		</section>
	);
};
